
import { useState } from 'react';
import React from 'react';

export default function Home() {
  const [activeTab, setActiveTab] = useState('support');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 文字数制限チェック
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    try {
      // 新しいフォームURL - Email Reminderを完全にオフにし、support@e-team.co.jpへの通知のみ
      const response = await fetch('https://readdy.ai/api/form/d3ife15soafcrsrcq3u0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        alert('お問い合わせを送信いたしました。担当者よりご連絡いたします。');
        form.reset();
      } else {
        // 取得したエラーメッセージがあれば表示、なければデフォルトエラー
        const errorMsg = await response.text().catch(() => '');
        throw new Error(errorMsg || '送信に失敗しました');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDemoClick = () => {
    window.open(
      'https://timerex.net/s/hideaki.nakamori_1809/e127051c',
      '_blank'
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/33e4d9a6aa73ec1d4a454a3f4917fe46/9b29f4596e40613ffaa52bea4a05c618.png"
                alt="E-team"
                className="h-8"
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap"
              >
                機能
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap"
              >
                料金
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap"
              >
                FAQ
              </button>
              <button
                onClick={handleDemoClick}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer whitespace-nowrap"
              >
                無料デモ・相談を申し込む
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 lg:py-32"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20office%20environment%20with%20AI%20technology%2C%20digital%20transformation%2C%20business%20professionals%20using%20advanced%20chatbot%20systems%2C%20clean%20minimalist%20workspace%20with%20computers%20and%20screens%20showing%20AI%20interfaces%2C%20bright%20natural%20lighting%2C%20professional%20atmosphere%2C%20high-tech%20business%20setting%20with%20blue%20and%20white%20color%20scheme&width=1920&height=1080&seq=hero-bg&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-white/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                業務効率化とマーケティングを両立する、<span className="text-blue-600">生成AIチャットボット</span>
              </h1>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                高効率／コスト削減／24時間対応／マーケティング支援を"ひとつで"。内製運用を前提にした導入と継続改善までサポート。
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Microsoft Azure OpenAI Service×国内サーバー。データ分離・サニタイズで安心運用。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleDemoClick}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap"
                >
                  無料デモ・相談を申し込む
                </button>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 cursor-pointer whitespace-nowrap"
                >
                  料金を見る
                </button>
              </div>
              <div className="mt-12">
                <p className="text-sm text-gray-600 mb-4">導入実績</p>
                <p className="text-gray-700">共創型チームが導入〜運用を支援</p>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20AI%20chatbot%20interface%20on%20computer%20screen%2C%20modern%20dashboard%20with%20conversation%20flows%2C%20clean%20UI%20design%2C%20business%20analytics%20charts%2C%20customer%20service%20automation%2C%20high-tech%20office%20environment%2C%20blue%20and%20white%20interface%20colors%2C%20professional%20business%20setting&width=600&height=400&seq=hero-image&orientation=landscape"
                alt="AIチャットボット管理画面"
                className="rounded-lg shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              こんなお悩み、ありませんか？
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              '高額な開発コストで導入を断念しがち',
              '支援コストを下げつつサービスレベルも維持したい',
              '24時間365日の対応ができず機会損失が発生',
              '英・中・韓など多言語対応が急務',
              '会話ログを活かしたニーズ可視化が不十分'
            ].map((problem, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                </div>
                <p className="text-gray-800 font-medium">{problem}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-600 text-white p-8 rounded-lg">
            <h3 className="text-2xl font-bold mb-4">E-teamの解決</h3>
            <p className="text-lg leading-relaxed">
              高額な開発コストを不要化。安価かつ高性能なソリューションを直感的な管理画面で運用可能（AIリテラシーや大規模予算がなくても
              OK）。
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              できること
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-chat-3-line',
                title: '自然な会話',
                description: 'LLMにより高度な前後文脈理解／会話継続が可能'
              },
              {
                icon: 'ri-global-line',
                title: '多言語は標準',
                description: '国際対応を見越した設計'
              },
              {
                icon: 'ri-database-2-line',
                title: '学習データ運用',
                description: 'FAQ・マニュアル・社内資料をアップロードして随時更新'
              },
              {
                icon: 'ri-settings-3-line',
                title: '応答チューニング',
                description: 'テスト→改善を管理画面から継続実施'
              },
              {
                icon: 'ri-bar-chart-line',
                title: 'ログ分析',
                description: '会話ログをマーケティングや事業改善へ活用'
              },
              {
                icon: 'ri-smartphone-line',
                title: '設置先の柔軟性',
                description: 'Web/アプリに埋め込み、複数プラットフォーム展開も可能'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-blue-600 text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              企業利用に必要なセキュリティを標準装備
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-microsoft-line',
                title: 'Microsoft Azure OpenAI Service',
                description: '信頼性の高いプラットフォームを採用'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'データ分離管理',
                description: '他社データと完全分離'
              },
              {
                icon: 'ri-map-pin-line',
                title: '国内サーバー',
                description: '日本国内での安全保管・法令対応'
              },
              {
                icon: 'ri-bug-line',
                title: 'サニタイズ処理',
                description: '悪意あるコード入力を除去'
              },
              {
                icon: 'ri-lock-line',
                title: '学習利用なし',
                description: '入力データはモデル学習に使用しない'
              }
            ].map((security, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${security.icon} text-green-600 text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {security.title}
                </h3>
                <p className="text-gray-700">{security.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              活用シーン
            </h2>
          </div>
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-full flex space-x-2">
              {[
                { id: 'support', label: 'カスタマーサポート' },
                { id: 'lead', label: '見込み顧客育成' },
                { id: 'global', label: 'グローバル対応' },
                { id: 'helpdesk', label: '社内ヘルプデスク' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-gray-50 p-8 rounded-lg">
            {activeTab === 'support' && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  カスタマーサポート
                </h3>
                <p className="text-gray-700 text-lg">
                  夜間・土日でも一次応答を自動化
                </p>
              </div>
            )}
            {activeTab === 'lead' && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  見込み顧客の育成
                </h3>
                <p className="text-gray-700 text-lg">
                  質問対応から資料請求・相談へ誘導
                </p>
              </div>
            )}
            {activeTab === 'global' && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  グローバル対応
                </h3>
                <p className="text-gray-700 text-lg">
                  標準の多言語で海外からの流入にも対応
                </p>
              </div>
            )}
            {activeTab === 'helpdesk' && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  社内ヘルプデスク
                </h3>
                <p className="text-gray-700 text-lg">
                  社内FAQの即時回答で工数削減
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Implementation Flow */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              導入は5ステップ。最短1〜2週間で公開
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-8">
            {[
              { step: '1', title: '学習データ準備', period: '約1週間' },
              { step: '2', title: 'データアップロード', period: '1〜3日' },
              { step: '3', title: 'テスト・チューニング', period: '約1週間' },
              { step: '4', title: '設置・公開', period: '約1週間' },
              { step: '5', title: '運用・調整・育成', period: '月1回推奨' }
            ].map((flow) => (
              <div key={flow.step} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {flow.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {flow.title}
                </h3>
                <p className="text-gray-600">{flow.period}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-blue-50 p-6 rounded-lg">
            <p className="text-gray-700 text-center">
              各ステップには専門サポートが伴走。公開後はログ分析→応答改善→CV導線強化のループを継続。
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              料金
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    初期費用
                  </h3>
                  <div className="text-4xl font-bold text-blue-600 mb-4">
                    200,000円
                    <span className="text-lg text-gray-600">
                      （税別）
                    </span>
                  </div>
                  <p className="text-gray-700">
                    初期設定／カスタマイズ／導入サポート
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    月額費用
                  </h3>
                  <div className="text-4xl font-bold text-blue-600 mb-4">
                    100,000円
                    <span className="text-lg text-gray-600">
                      （税別）
                    </span>
                  </div>
                  <p className="text-gray-700">
                    管理画面でトークン上限設定可（予想外コストの抑制）
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-bold text-green-800 mb-2">
                無料トライアル：1ヶ月
              </h3>
              <p className="text-green-700">
                ※初期導入費用は発生
              </p>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
              <p>• 追加サービス（例：定期レポート等）は別途費用（要相談）</p>
              <p>• 契約は1ヶ月〜自動更新</p>
              <p>• 解約は希望日を含め31日前までの連絡で可能</p>
            </div>
            <div className="text-center mt-8">
              <button
                onClick={handleDemoClick}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap"
              >
                最適プランを相談する
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              導入効果の一例
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-time-line',
                title: '夜間対応の一次応答を自動化',
                description: '問い合わせ取りこぼし減／満足度向上'
              },
              {
                icon: 'ri-global-line',
                title: '多言語での問合せ窓口を一本化',
                description: '海外CVの獲得に寄与'
              },
              {
                icon: 'ri-question-answer-line',
                title: 'FAQ散在の統合',
                description: '一次解決率の向上／担当者の負荷軽減'
              }
            ].map((caseStudy, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${caseStudy.icon} text-blue-600 text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {caseStudy.title}
                </h3>
                <p className="text-gray-700">{caseStudy.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              よくあるご質問
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                question: '生成AIの回答はどの程度正確ですか？',
                answer:
                  '常に100％ではありません。送信部に相談導線を用意し有人対応と連携します。チャット経由のリード獲得にもつながります。'
              },
              {
                question: 'どのように学習させますか？',
                answer:
                  'FAQ／マニュアル／社内資料を管理画面からアップロード。既存データの編集・調整もリアルタイムで可能です。'
              },
              {
                question: '公開までの期間は？',
                answer:
                  '学習データ準備状況にもよりますが、全体で1〜4週間が目安。導入は最短1〜2週間程度、公開はその後1〜2週間が想定です。'
              },
              {
                question: 'セキュリティは安全ですか？',
                answer:
                  'Microsoft Azure OpenAI Serviceの採用、データ分離管理、国内サーバー、サニタイズ処理を実施。入力データはモデル学習に未使用です。'
              }
            ].map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === idx ? null : idx)
                  }
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                >
                  <span className="font-semibold text-gray-900">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-${openFaq === idx ? 'up' : 'down'}-s-line text-gray-500`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              運営会社
            </h2>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  会社概要
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium">社名：</span>株式会社E-team
                  </div>
                  <div>
                    <span className="font-medium">代表：</span>江口 弘起
                  </div>
                  <div>
                    <span className="font-medium">設立：</span>2020年1月8日
                  </div>
                  <div>
                    <span className="font-medium">メンバー：</span>
                    20名（業務委託含む／2025年8月時点）
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  事業内容
                </h3>
                <div className="space-y-2">
                  <div>• マーケティングDX支援</div>
                  <div>• HR支援</div>
                  <div>• 生成AI研修</div>
                </div>
                <div className="mt-6">
                  <div>
                    <span className="font-medium">理念：</span>
                    「共創型組織で人材ロスをゼロに」
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div>
                <span className="font-medium">所在地：</span>
                〒150-0012 東京都渋谷区広尾1-3-18 広尾オフィスビル11F
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            まずは無料デモから
          </h2>
          <p className="text-xl mb-8">
            オンライン30分で、導入可否・費用感・進め方を具体化
          </p>
          <button
            onClick={handleDemoClick}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 cursor-pointer whitespace-nowrap"
          >
            無料デモ・相談を申し込む
          </button>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              お問い合わせ
            </h2>
          </div>
          <form onSubmit={handleFormSubmit} data-readdy-form className="space-y-6">
            {/* Company & Name */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Purpose & Department */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="purpose"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  導入目的
                </label>
                <select
                  id="purpose"
                  name="purpose"
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">選択してください</option>
                  <option value="カスタマーサポート">カスタマーサポート</option>
                  <option value="見込み顧客育成">見込み顧客育成</option>
                  <option value="グローバル対応">グローバル対応</option>
                  <option value="社内ヘルプデスク">社内ヘルプデスク</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="department"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  部署
                </label>
                <select
                  id="department"
                  name="department"
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">選択してください</option>
                  <option value="営業部">営業部</option>
                  <option value="マーケティング部">マーケティング部</option>
                  <option value="開発部">開発部</option>
                  <option value="カスタマーサポート部">カスタマーサポート部</option>
                </select>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label
                htmlFor="timeline"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                想定導入時期
              </label>
              <select
                id="timeline"
                name="timeline"
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">選択してください</option>
                <option value="1ヶ月以内">1ヶ月以内</option>
                <option value="1〜3ヶ月">1〜3ヶ月</option>
                <option value="3〜6ヶ月">3〜6ヶ月</option>
                <option value="6ヶ月以上">6ヶ月以上</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="500文字以内でご入力ください"
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">500文字以内</p>
            </div>

            {/* Submit */}
            <div className="text-center">
              <button
                type="submit"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap"
              >
                送信する
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <img
                src="https://static.readdy.ai/image/33e4d9a6aa73ec1d4a454a3f4917fe46/9b29f4596e40613ffaa52bea4a05c618.png"
                alt="E-team"
                className="h-8 mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400">
                生成AIチャットボットで業務効率化とマーケティングを両立
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">会社情報</h4>
              <div className="text-gray-400 space-y-2">
                <div>
                  <a
                    href="https://e-team.co.jp/company/"
                    className="hover:underline"
                  >
                    会社概要
                  </a>
                </div>
                <div>〒150-0012 東京都渋谷区広尾1-3-18</div>
                <div>広尾オフィスビル11F</div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-4 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} E-team. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Fixed Mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 md:hidden bg-blue-600 text-white p-4 flex justify-center">
        <button
          onClick={handleDemoClick}
          className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100"
        >
          無料デモ・相談を申し込む
        </button>
      </div>
    </div>
  );
}
